'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslations } from 'next-intl';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import {
  ArrowDownAZ,
  ArrowUpZA,
  Check,
  ChevronDown,
  ChevronsDownUp,
  ChevronsUpDown,
  Search,
  Sparkles
} from 'lucide-react';
import { Icon } from '@iconify/react';
import '@/lib/iconify-solar';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from '@/components/ui/collapsible';
import { permissionQueries } from '../services/roles.queries';
import {
  dynamicFormKeys,
  dynamicFormsApi,
  type DynamicForm
} from '@/features/dynamic-forms';
import { groupPermissions } from '../mappers/role.mapper';
import {
  GROUP_ICON_MAP,
  GROUP_KEY_MAP,
  GROUP_FEATURE_NAMESPACE,
  PERMISSION_CATEGORIES,
  CHILD_GROUP_MAP,
  getGroupCategory,
  isColumnPermission,
  isDynamicColumnPermission,
  isHiddenPermission,
  getColumnField,
  snakeToCamel
} from './role-permissions-matrix-config';
import { useHiddenPermissionGroups } from './use-hidden-permission-groups';
import type { Permission } from '../types';

const VIEW_FILTER_ACTION = 'view_filter';

function isViewFilterPermission(name: string): boolean {
  return name.split('.')[1] === VIEW_FILTER_ACTION;
}

/**
 * Panel for the view-related half of a role's permissions:
 *   - {group}.view_filter — the View Columns button
 *   - {group}.column.{field} — static column visibility
 *   - {group}.dynamic_column.{field_id} — dynamic-form-driven column visibility
 *
 * Fully controlled: it owns no permission state of its own, so it stays in
 * lockstep with whatever surface hosts it. It used to be a dialog with its
 * own Save button, which meant a role's view permissions were edited in a
 * different place, on a different save cycle, from its action permissions.
 * It is now embedded as a tab inside the one role permissions editor and
 * writes through the host's autosave.
 */
interface RoleViewPermissionsPanelProps {
  /** Every permission the role holds — non-view names are passed through untouched. */
  permissions: string[];
  /** Receives the next full permissions array on every toggle. */
  onChange: (next: string[]) => void;
  /** When true, all toggles are disabled. */
  readOnly?: boolean;
}

export function RoleViewPermissionsPanel({
  permissions,
  onChange,
  readOnly = false
}: RoleViewPermissionsPanelProps) {
  const t = useTranslations('roles');
  const tRoot = useTranslations();
  // Derived, never stored: the host owns the permission set, so there is no
  // second copy here to drift out of sync with it.
  const local = useMemo(() => new Set(permissions), [permissions]);
  const [search, setSearch] = useState('');
  // Map of group → explicit user choice. Missing key falls back to the default
  // (open while searching or sidebar-filtered, closed otherwise) so the
  // collapse-all button can override the auto-expand behavior.
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(
    () => ({})
  );
  const [activityFilter, setActivityFilter] = useState<
    'all' | 'active' | 'inactive'
  >('all');
  const [sortOrder, setSortOrder] = useState<
    'default' | 'name_asc' | 'name_desc' | 'active_desc'
  >('name_asc');
  // Sidebar selection: 'all' (default) | category id | { group: name }.
  // Categories filter the main pane to every group in the category;
  // groups narrow further to a single group.
  const [selection, setSelection] = useState<
    | { kind: 'all' }
    | { kind: 'category'; id: string }
    | { kind: 'group'; id: string }
  >({ kind: 'all' });
  const [openCategories, setOpenCategories] = useState<Set<string>>(
    () => new Set(PERMISSION_CATEGORIES.map((c) => c.id))
  );

  const { data: permissionsData } = useQuery(permissionQueries.list());
  const allPermissions = useMemo(
    () => permissionsData?.data ?? [],
    [permissionsData]
  );

  // Per-type dynamic-form filter state. One Select per form `type` that has
  // at least one form (registration / filling / audit / delivery), each
  // independently narrowing the visible dynamic_column.* perms whose field
  // belongs to a form of that type. `'all'` is the type-scoped wildcard.
  const [formFiltersByType, setFormFiltersByType] = useState<
    Record<string, 'all' | number>
  >({});

  const setFormFilterForType = useCallback(
    (type: string, value: 'all' | number) => {
      setFormFiltersByType((prev) => ({ ...prev, [type]: value }));
    },
    []
  );

  // Paginate through ALL forms with their fields — avoids the N+1 fan-out
  // (1 list + N detail calls) and handles instances with more than 100 forms
  // by auto-fetching subsequent pages until hasNext is false.
  const {
    data: formsPages,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage
  } = useInfiniteQuery({
    queryKey: [...dynamicFormKeys.lists(), { with_fields: true }],
    queryFn: ({ pageParam }) =>
      dynamicFormsApi.getAll({
        page: pageParam as number,
        per_page: 100,
        with_fields: true
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.pagination.hasNext ? lastPage.pagination.page + 1 : undefined
  });

  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) {
      void fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const allForms = useMemo<DynamicForm[]>(
    () => (formsPages?.pages.flatMap((p) => p.data) ?? []) as DynamicForm[],
    [formsPages]
  );

  // Group forms by type so we can render one Select per type-with-forms.
  // Sorted by type for stable ordering across renders.
  const formsByType = useMemo(() => {
    const map = new Map<string, DynamicForm[]>();
    for (const form of allForms) {
      if (!map.has(form.type)) map.set(form.type, []);
      map.get(form.type)!.push(form);
    }
    return Array.from(map.entries())
      .map(([type, forms]) => ({
        type,
        forms: forms.slice().sort((a, b) => a.name.localeCompare(b.name))
      }))
      .sort((a, b) => a.type.localeCompare(b.type));
  }, [allForms]);

  // field_id → { form_id, form_type, form_name, label } so we can both filter
  // by type-scoped Select and render real Arabic/English labels instead of
  // `Form Field #{id}`.
  const fieldsById = useMemo(() => {
    const map = new Map<
      number,
      {
        formId: number;
        formType: string;
        formName: string;
        label: string;
      }
    >();
    for (const form of allForms) {
      for (const field of form.fields ?? []) {
        map.set(field.id, {
          formId: form.id,
          formType: form.type,
          formName: form.name,
          label: field.label
        });
      }
    }
    return map;
  }, [allForms]);

  // Resolve a column label by pulling the actual table column header from
  // the feature's i18n namespace (so the dialog matches the listing exactly).
  // Lookup chain:
  //   1. `{featureNamespace}.{camelField}` — the table header key
  //   2. `{featureNamespace}.{snakeField}` — for namespaces using snake_case
  //   3. `roles.columns.{field}` — generic fallback shared by groups
  //   4. raw field name
  // Messages with ICU placeholders (e.g. "{count} صندوق") are skipped because
  // they're cell formatters, not column headers — calling t() on them throws.
  const fieldLabel = useCallback(
    (group: string, field: string): string => {
      const ns = GROUP_FEATURE_NAMESPACE[group];
      if (ns) {
        const camelKey = `${ns}.${snakeToCamel(field)}`;
        const camelHit = readPlainMessage(tRoot, camelKey);
        if (camelHit !== undefined) return camelHit;
        const snakeKey = `${ns}.${field}`;
        const snakeHit = readPlainMessage(tRoot, snakeKey);
        if (snakeHit !== undefined) return snakeHit;
      }
      const fallbackHit = readPlainMessage(t, `columns.${field}`);
      if (fallbackHit !== undefined) return fallbackHit;
      return field;
    },
    [t, tRoot]
  );

  const hiddenGroups = useHiddenPermissionGroups();

  // Restrict to view-related permissions, dropping anything that belongs
  // to a runtime-hidden group (a feature-flagged group currently disabled).
  const viewPermissions = useMemo<Permission[]>(
    () =>
      allPermissions.filter(
        (p) =>
          (isViewFilterPermission(p.name) || isColumnPermission(p.name)) &&
          !isHiddenPermission(p.name, hiddenGroups)
      ),
    [allPermissions, hiddenGroups]
  );

  // Drives the form-filter row visibility — hide the Selects entirely when
  // no dynamic_column.* perms exist, so non-registrations roles don't see them.
  const hasAnyDynamicPerm = useMemo(
    () => viewPermissions.some((p) => isDynamicColumnPermission(p.name)),
    [viewPermissions]
  );

  // A dynamic perm passes when:
  //   - it has no associated form (orphan): always shown,
  //   - the type-filter for its form's type is `'all'`,
  //   - or the type-filter equals its specific form id.
  const passesFormFilters = useCallback(
    (permName: string): boolean => {
      const id = Number(getColumnField(permName));
      const meta = Number.isFinite(id) ? fieldsById.get(id) : undefined;
      if (!meta) return true;
      const filter = formFiltersByType[meta.formType] ?? 'all';
      return filter === 'all' || filter === meta.formId;
    },
    [fieldsById, formFiltersByType]
  );

  const groups = useMemo(
    () => groupPermissions(viewPermissions),
    [viewPermissions]
  );

  const getGroupLabel = useCallback(
    (group: string) => {
      const key = GROUP_KEY_MAP[group] ?? 'group_other';
      return t(key);
    },
    [t]
  );

  const filteredGroups = useMemo(() => {
    if (!search.trim()) return groups;
    const q = search.toLowerCase().trim();
    return groups
      .map(({ group, permissions: perms }) => {
        if (getGroupLabel(group).toLowerCase().includes(q)) {
          return { group, permissions: perms };
        }
        const filtered = perms.filter((perm) => {
          const field = getColumnField(perm.name);
          return (
            perm.name.toLowerCase().includes(q) ||
            field.toLowerCase().includes(q)
          );
        });
        return { group, permissions: filtered };
      })
      .filter(({ permissions: perms }) => perms.length > 0);
  }, [groups, search, getGroupLabel]);

  const togglePermission = useCallback(
    (name: string) => {
      const next = new Set(permissions);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      onChange(Array.from(next));
    },
    [permissions, onChange]
  );

  const toggleMany = useCallback(
    (names: string[], on: boolean) => {
      const next = new Set(permissions);
      if (on) names.forEach((n) => next.add(n));
      else names.forEach((n) => next.delete(n));
      onChange(Array.from(next));
    },
    [permissions, onChange]
  );

  const toggleGroupOpen = useCallback((group: string, open: boolean) => {
    setOpenGroups((prev) => ({ ...prev, [group]: open }));
  }, []);

  const totalCount = viewPermissions.length;
  const activeCount = useMemo(
    () => viewPermissions.filter((p) => local.has(p.name)).length,
    [viewPermissions, local]
  );

  // Visibility filter: drop groups whose total active state doesn't match
  // the chosen pill. Search-narrowed groups are filtered first.
  const filteredByActivity = useMemo(() => {
    if (activityFilter === 'all') return filteredGroups;
    return filteredGroups.filter(({ permissions: perms }) => {
      const active = perms.filter((p) => local.has(p.name)).length;
      if (activityFilter === 'active') return active > 0;
      return active === 0; // inactive
    });
  }, [filteredGroups, local, activityFilter]);

  // Group-level sort. `default` keeps server order; the rest use the
  // translated group label or the active-count for stable visual ordering.
  const sortedGroups = useMemo(() => {
    if (sortOrder === 'default') return filteredByActivity;
    const list = [...filteredByActivity];
    if (sortOrder === 'active_desc') {
      list.sort((a, b) => {
        const aActive = a.permissions.filter((p) => local.has(p.name)).length;
        const bActive = b.permissions.filter((p) => local.has(p.name)).length;
        return (
          bActive - aActive ||
          getGroupLabel(a.group).localeCompare(getGroupLabel(b.group))
        );
      });
      return list;
    }
    list.sort((a, b) =>
      getGroupLabel(a.group).localeCompare(getGroupLabel(b.group))
    );
    if (sortOrder === 'name_desc') list.reverse();
    return list;
  }, [filteredByActivity, sortOrder, local, getGroupLabel]);

  // Narrow the main pane based on sidebar selection.
  //   all       → every group passing search + activity filter
  //   category  → only groups whose category matches
  //   group     → only the chosen group (single-row deep dive)
  const visibleGroups = useMemo(() => {
    if (selection.kind === 'all') return sortedGroups;
    if (selection.kind === 'category') {
      return sortedGroups.filter(
        (g) => getGroupCategory(g.group) === selection.id
      );
    }
    return sortedGroups.filter((g) => g.group === selection.id);
  }, [sortedGroups, selection]);

  // Map of parent group → its visible child groups, derived from
  // CHILD_GROUP_MAP. The top-level render skips entries whose parent is
  // also visible, and renders them nested inside the parent's content
  // block — same pattern as role-permissions-panel.tsx.
  const childGroupsByParent = useMemo<
    Record<string, (typeof visibleGroups)[number][]>
  >(() => {
    const visibleSet = new Set(visibleGroups.map((g) => g.group));
    const map: Record<string, (typeof visibleGroups)[number][]> = {};
    for (const entry of visibleGroups) {
      const parent = CHILD_GROUP_MAP[entry.group];
      if (parent && visibleSet.has(parent)) {
        (map[parent] ??= []).push(entry);
      }
    }
    return map;
  }, [visibleGroups]);

  const hasVisibleParent = useCallback(
    (group: string) => {
      const parent = CHILD_GROUP_MAP[group];
      return !!parent && visibleGroups.some((g) => g.group === parent);
    },
    [visibleGroups]
  );

  // Permissions currently visible to the user after every layer of
  // narrowing (search, activity, sidebar selection, per-type form filter).
  // The bulk-action chips below act on exactly this set so a click does
  // what the user sees — never silently flipping hidden perms.
  const visiblePermissions = useMemo<Permission[]>(() => {
    const out: Permission[] = [];
    for (const { permissions: perms } of visibleGroups) {
      for (const perm of perms) {
        if (
          isDynamicColumnPermission(perm.name) &&
          !passesFormFilters(perm.name)
        ) {
          continue;
        }
        out.push(perm);
      }
    }
    return out;
  }, [visibleGroups, passesFormFilters]);

  const visibleByKind = useMemo(() => {
    const viewFilters: Permission[] = [];
    const columns: Permission[] = [];
    const dynamics: Permission[] = [];
    for (const perm of visiblePermissions) {
      if (isViewFilterPermission(perm.name)) {
        viewFilters.push(perm);
      } else if (isDynamicColumnPermission(perm.name)) {
        dynamics.push(perm);
      } else if (isColumnPermission(perm.name)) {
        columns.push(perm);
      }
    }
    return { viewFilters, columns, dynamics };
  }, [visiblePermissions]);

  const visibleActiveCount = useMemo(
    () => visiblePermissions.filter((p) => local.has(p.name)).length,
    [visiblePermissions, local]
  );

  const toggleAllOf = useCallback(
    (perms: Permission[]) => {
      if (perms.length === 0) return;
      const names = perms.map((p) => p.name);
      const allActive = names.every((n) => local.has(n));
      toggleMany(names, !allActive);
    },
    [local, toggleMany]
  );

  // Default behavior: groups auto-expand when the user is searching or has
  // narrowed via the sidebar (so matches stay visible). The user can still
  // override that default per group, or use the collapse/expand-all button,
  // which is why we look up explicit choices first.
  const isSearching = search.trim().length > 0;
  const isFiltered = selection.kind !== 'all';
  const isOpen = useCallback(
    (group: string) => {
      const explicit = openGroups[group];
      if (explicit !== undefined) return explicit;
      return isSearching || isFiltered;
    },
    [isSearching, isFiltered, openGroups]
  );

  const toggleCategoryOpen = useCallback((id: string, open: boolean) => {
    setOpenCategories((prev) => {
      const next = new Set(prev);
      if (open) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);

  // Build a per-group lookup for sidebar nesting + counts. Search narrows
  // which groups are eligible for display, mirroring the main pane.
  const sidebarGroupsByCategory = useMemo(() => {
    const eligible = new Map(filteredGroups.map((g) => [g.group, g]));
    return PERMISSION_CATEGORIES.map((cat) => ({
      ...cat,
      groupsInCategory: cat.groups
        .map((g) => eligible.get(g))
        .filter((g): g is (typeof filteredGroups)[number] => Boolean(g))
    }));
  }, [filteredGroups]);

  // Permission groups with no category mapping — surface them in a fallback
  // bucket so nothing silently disappears if PERMISSION_CATEGORIES drifts.
  const uncategorizedGroups = useMemo(() => {
    return filteredGroups.filter((g) => getGroupCategory(g.group) === null);
  }, [filteredGroups]);

  const expandAll = useCallback(() => {
    setOpenGroups((prev) => {
      const next = { ...prev };
      for (const g of visibleGroups) next[g.group] = true;
      return next;
    });
  }, [visibleGroups]);

  const collapseAll = useCallback(() => {
    setOpenGroups((prev) => {
      const next = { ...prev };
      for (const g of visibleGroups) next[g.group] = false;
      return next;
    });
  }, [visibleGroups]);

  // True only when every visible group is currently expanded — used to flip
  // the Expand-All / Collapse-All button to its opposite intent. Uses the
  // effective state (explicit choice + default), not just explicit, so the
  // button reflects what the user actually sees.
  const allExpanded =
    visibleGroups.length > 0 && visibleGroups.every((g) => isOpen(g.group));

  // Recursive group block. `isChild=true` swaps the outer border/icon
  // tile for a lighter look and indents the content so nested groups
  // (CHILD_GROUP_MAP) read as a sub-section of their parent.
  function renderGroup(
    entry: (typeof visibleGroups)[number],
    isChild: boolean
  ) {
    const { group, permissions: perms } = entry;
    const viewFilterPerm = perms.find((p) => isViewFilterPermission(p.name));
    const columnPerms = perms
      .filter(
        (p) => isColumnPermission(p.name) && !isDynamicColumnPermission(p.name)
      )
      .slice()
      .sort((a, b) =>
        fieldLabel(group, getColumnField(a.name)).localeCompare(
          fieldLabel(group, getColumnField(b.name))
        )
      );
    const dynamicPerms = perms
      .filter((p) => isDynamicColumnPermission(p.name))
      .filter((p) => passesFormFilters(p.name))
      .slice()
      .sort((a, b) => {
        const aId = Number(getColumnField(a.name));
        const bId = Number(getColumnField(b.name));
        return (
          (Number.isFinite(aId) ? aId : 0) - (Number.isFinite(bId) ? bId : 0)
        );
      });
    const allNames = perms.map((p) => p.name);
    const activeInGroup = allNames.filter((n) => local.has(n)).length;
    const allActive = activeInGroup === perms.length;
    const someActive = activeInGroup > 0 && activeInGroup < perms.length;
    const groupKey = GROUP_KEY_MAP[group] ?? 'group_other';
    const icon = GROUP_ICON_MAP[group];
    const children = childGroupsByParent[group] ?? [];

    return (
      <Collapsible
        key={group}
        open={isOpen(group)}
        onOpenChange={(o) => toggleGroupOpen(group, o)}
      >
        <div
          className={cn(
            'flex items-center gap-2 rounded-md px-3 py-1.5 transition-colors',
            isChild ? 'hover:bg-muted/40 border' : 'hover:bg-muted/50 border',
            allActive && 'border-emerald-500/30 bg-emerald-500/5'
          )}
        >
          <Checkbox
            checked={allActive ? true : someActive ? 'indeterminate' : false}
            disabled={readOnly}
            onCheckedChange={(v) => toggleMany(allNames, !!v)}
          />
          <CollapsibleTrigger className='group flex flex-1 items-center gap-2 text-start'>
            {icon && (
              <div
                className={cn(
                  'flex shrink-0 items-center justify-center rounded-md',
                  isChild ? 'h-5 w-5' : 'h-6 w-6',
                  allActive
                    ? 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400'
                    : 'bg-muted text-muted-foreground'
                )}
              >
                <Icon icon={icon} className='h-3.5 w-3.5' />
              </div>
            )}
            <span
              className={cn(
                'flex-1 font-medium',
                isChild ? 'text-xs' : 'text-sm'
              )}
            >
              {t(groupKey)}
            </span>
            <span
              className={cn(
                'text-xs tabular-nums',
                allActive
                  ? 'text-emerald-600 dark:text-emerald-400'
                  : activeInGroup > 0
                    ? 'text-foreground'
                    : 'text-muted-foreground'
              )}
            >
              {activeInGroup}/{perms.length}
            </span>
            <ChevronDown className='text-muted-foreground h-4 w-4 shrink-0 transition-transform group-data-[state=open]:rotate-180' />
          </CollapsibleTrigger>
        </div>
        <CollapsibleContent>
          <div className='ms-7 mt-1 space-y-1 ps-3'>
            {viewFilterPerm && (
              <PermRow
                label={t('action_view_filter')}
                description={t('viewFilterDescription')}
                checked={local.has(viewFilterPerm.name)}
                disabled={readOnly}
                onChange={() => togglePermission(viewFilterPerm.name)}
              />
            )}
            {columnPerms.length > 0 && (
              <Section title={t('columnPermissions')}>
                {columnPerms.map((perm) => (
                  <PermRow
                    key={perm.id}
                    label={fieldLabel(group, getColumnField(perm.name))}
                    checked={local.has(perm.name)}
                    disabled={readOnly}
                    onChange={() => togglePermission(perm.name)}
                  />
                ))}
              </Section>
            )}
            {dynamicPerms.length > 0 && (
              <Section title={t('dynamicColumnPermissions')}>
                {dynamicPerms.map((perm) => {
                  const rawId = getColumnField(perm.name);
                  const meta = fieldsById.get(Number(rawId));
                  return (
                    <PermRow
                      key={perm.id}
                      label={
                        meta
                          ? meta.label
                          : `${t('dynamicFieldLabel')} #${rawId}`
                      }
                      description={meta?.formName}
                      checked={local.has(perm.name)}
                      disabled={readOnly}
                      onChange={() => togglePermission(perm.name)}
                      mono={!meta}
                    />
                  );
                })}
              </Section>
            )}
            {children.length > 0 && (
              <div className='border-muted ms-2 space-y-1 border-s ps-2'>
                {children.map((child) => renderGroup(child, true))}
              </div>
            )}
          </div>
        </CollapsibleContent>
      </Collapsible>
    );
  }

  return (
    <div className='flex min-h-0 flex-1 flex-col gap-2'>
      <p className='text-muted-foreground shrink-0 text-xs'>
        {t('viewPermissionsDescription')}
      </p>

      <div className='flex max-h-[40svh] shrink-0 flex-col gap-1.5 overflow-y-auto pe-1 sm:max-h-none sm:gap-2 sm:overflow-visible sm:pe-0'>
        <div className='flex items-center gap-2'>
          <div className='relative flex-1'>
            <Search className='text-muted-foreground pointer-events-none absolute start-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2' />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('searchPermissions')}
              className='h-9 ps-8'
            />
          </div>
          <Button
            type='button'
            variant='outline'
            size='sm'
            onClick={allExpanded ? collapseAll : expandAll}
            disabled={visibleGroups.length === 0}
            aria-label={allExpanded ? t('collapseAll') : t('expandAll')}
            title={allExpanded ? t('collapseAll') : t('expandAll')}
            className='shrink-0'
          >
            {allExpanded ? (
              <ChevronsDownUp className='h-3.5 w-3.5 sm:me-1' />
            ) : (
              <ChevronsUpDown className='h-3.5 w-3.5 sm:me-1' />
            )}
            <span className='hidden sm:inline'>
              {allExpanded ? t('collapseAll') : t('expandAll')}
            </span>
          </Button>
        </div>
        <div className='flex items-center justify-between gap-2'>
          <div className='flex flex-wrap items-center gap-1'>
            <FilterPill
              active={activityFilter === 'all'}
              onClick={() => setActivityFilter('all')}
            >
              {t('filterAll')}
            </FilterPill>
            <FilterPill
              active={activityFilter === 'active'}
              onClick={() => setActivityFilter('active')}
            >
              {t('filterActive')}
            </FilterPill>
            <FilterPill
              active={activityFilter === 'inactive'}
              onClick={() => setActivityFilter('inactive')}
            >
              {t('filterInactive')}
            </FilterPill>
          </div>
          <Select
            value={sortOrder}
            onValueChange={(v) => setSortOrder(v as typeof sortOrder)}
          >
            <SelectTrigger className='h-7 w-auto gap-1 px-2 text-xs'>
              <SelectValue />
            </SelectTrigger>
            <SelectContent align='end'>
              <SelectItem value='name_asc'>
                <span className='flex items-center gap-1.5'>
                  <ArrowDownAZ className='h-3.5 w-3.5' />
                  {t('sortNameAsc')}
                </span>
              </SelectItem>
              <SelectItem value='name_desc'>
                <span className='flex items-center gap-1.5'>
                  <ArrowUpZA className='h-3.5 w-3.5' />
                  {t('sortNameDesc')}
                </span>
              </SelectItem>
              <SelectItem value='active_desc'>
                <span className='flex items-center gap-1.5'>
                  <Sparkles className='h-3.5 w-3.5' />
                  {t('sortActiveDesc')}
                </span>
              </SelectItem>
              <SelectItem value='default'>{t('sortDefault')}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {/* Mobile category picker — replaces the hidden sidebar on small screens */}
        <Select
          value={
            selection.kind === 'all'
              ? 'all'
              : selection.kind === 'category'
                ? `cat:${selection.id}`
                : `group:${selection.id}`
          }
          onValueChange={(v) => {
            if (v === 'all') setSelection({ kind: 'all' });
            else if (v.startsWith('cat:'))
              setSelection({ kind: 'category', id: v.slice(4) });
            else if (v.startsWith('group:'))
              setSelection({ kind: 'group', id: v.slice(6) });
          }}
        >
          <SelectTrigger className='h-9 w-full text-sm md:hidden'>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value='all'>
              {t('filterAll')} · {activeCount}/{totalCount}
            </SelectItem>
            {sidebarGroupsByCategory.map(
              ({ id, i18nKey, groupsInCategory }) => {
                if (groupsInCategory.length === 0) return null;
                const allPerms = groupsInCategory.flatMap((g) => g.permissions);
                const active = allPerms.filter((p) => local.has(p.name)).length;
                return (
                  <SelectItem key={id} value={`cat:${id}`}>
                    {readPlainMessage(tRoot, i18nKey) ?? i18nKey} · {active}/
                    {allPerms.length}
                  </SelectItem>
                );
              }
            )}
            {selection.kind === 'group' &&
              (() => {
                const groupKey = GROUP_KEY_MAP[selection.id] ?? 'group_other';
                return (
                  <SelectItem value={`group:${selection.id}`}>
                    {t(groupKey)}
                  </SelectItem>
                );
              })()}
          </SelectContent>
        </Select>

        {hasAnyDynamicPerm && formsByType.length > 0 && (
          <div className='-mx-1 flex items-center gap-2 overflow-x-auto px-1 pb-1 sm:mx-0 sm:flex-wrap sm:gap-3 sm:px-0 sm:pb-0'>
            {formsByType.map(({ type, forms }) => {
              const value = formFiltersByType[type] ?? 'all';
              return (
                <div key={type} className='flex shrink-0 items-center gap-2'>
                  <span className='text-muted-foreground shrink-0 text-xs'>
                    {t('dynamicFormFilterByType', {
                      type:
                        readPlainMessage(
                          tRoot,
                          `dynamicForms.type${type.charAt(0).toUpperCase()}${type.slice(1)}`
                        ) ?? type
                    })}
                  </span>
                  <Select
                    value={String(value)}
                    onValueChange={(v) =>
                      setFormFilterForType(
                        type,
                        v === 'all' ? 'all' : Number(v)
                      )
                    }
                  >
                    <SelectTrigger className='h-7 w-auto min-w-[160px] gap-1 px-2 text-xs'>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value='all'>
                        {t('dynamicFormFilterAll')}
                      </SelectItem>
                      {forms.map((f) => (
                        <SelectItem key={f.id} value={String(f.id)}>
                          {f.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              );
            })}
          </div>
        )}
        {!readOnly && visiblePermissions.length > 0 && (
          <div className='-mx-1 flex items-center gap-1.5 overflow-x-auto px-1 pb-1 sm:mx-0 sm:flex-wrap sm:px-0 sm:pb-0'>
            <BulkChip
              label={
                visibleActiveCount === visiblePermissions.length
                  ? t('bulkDeselectAllVisible')
                  : t('bulkSelectAllVisible')
              }
              activeCount={visibleActiveCount}
              totalCount={visiblePermissions.length}
              onClick={() => toggleAllOf(visiblePermissions)}
              variant='primary'
            />
            {visibleByKind.viewFilters.length > 0 && (
              <BulkChip
                label={t('bulkAllViewFilters')}
                activeCount={
                  visibleByKind.viewFilters.filter((p) => local.has(p.name))
                    .length
                }
                totalCount={visibleByKind.viewFilters.length}
                onClick={() => toggleAllOf(visibleByKind.viewFilters)}
              />
            )}
            {visibleByKind.columns.length > 0 && (
              <BulkChip
                label={t('bulkAllColumns')}
                activeCount={
                  visibleByKind.columns.filter((p) => local.has(p.name)).length
                }
                totalCount={visibleByKind.columns.length}
                onClick={() => toggleAllOf(visibleByKind.columns)}
              />
            )}
            {visibleByKind.dynamics.length > 0 && (
              <BulkChip
                label={t('bulkAllDynamicColumns')}
                activeCount={
                  visibleByKind.dynamics.filter((p) => local.has(p.name)).length
                }
                totalCount={visibleByKind.dynamics.length}
                onClick={() => toggleAllOf(visibleByKind.dynamics)}
              />
            )}
          </div>
        )}
        <p className='text-muted-foreground text-xs'>
          {t('activePermissions', {
            count: activeCount,
            total: totalCount
          })}
        </p>
      </div>

      <div className='flex min-h-0 flex-1 gap-3 overflow-hidden'>
        {/* Sidebar — category navigation */}
        <aside className='bg-muted/30 hidden shrink-0 flex-col rounded-md border md:flex'>
          <div className='flex shrink-0 items-center justify-between gap-2 border-b px-2 py-1.5'>
            <span className='text-muted-foreground text-xs font-semibold tracking-wide uppercase'>
              {t('categoriesTitle')}
            </span>
            {selection.kind !== 'all' && (
              <button
                type='button'
                onClick={() => setSelection({ kind: 'all' })}
                className='text-primary text-xs hover:underline'
              >
                {t('clearCategory')}
              </button>
            )}
          </div>
          <ScrollArea className='min-h-0 flex-1'>
            <div className='space-y-0.5 p-1.5'>
              <CategoryRow
                active={selection.kind === 'all'}
                onClick={() => setSelection({ kind: 'all' })}
                label={t('filterAll')}
                count={activeCount}
                total={totalCount}
              />

              {sidebarGroupsByCategory.map(
                ({ id, i18nKey, groupsInCategory }) => {
                  if (groupsInCategory.length === 0) return null;
                  const allPerms = groupsInCategory.flatMap(
                    (g) => g.permissions
                  );
                  const active = allPerms.filter((p) =>
                    local.has(p.name)
                  ).length;
                  const isExpanded = openCategories.has(id);
                  return (
                    <Collapsible
                      key={id}
                      open={isExpanded}
                      onOpenChange={(o) => toggleCategoryOpen(id, o)}
                    >
                      <div className='flex items-stretch gap-0.5'>
                        <CategoryRow
                          active={
                            selection.kind === 'category' && selection.id === id
                          }
                          onClick={() => setSelection({ kind: 'category', id })}
                          label={readPlainMessage(tRoot, i18nKey) ?? i18nKey}
                          count={active}
                          total={allPerms.length}
                        />
                        <CollapsibleTrigger
                          className='hover:bg-muted text-muted-foreground rounded-md px-1.5'
                          aria-label={
                            isExpanded ? t('collapseAll') : t('expandAll')
                          }
                        >
                          <ChevronDown
                            className={cn(
                              'h-3.5 w-3.5 transition-transform',
                              isExpanded && 'rotate-180'
                            )}
                          />
                        </CollapsibleTrigger>
                      </div>
                      <CollapsibleContent>
                        <div className='ms-3 space-y-0.5 border-s ps-1.5'>
                          {(() => {
                            const inCategory = new Set(
                              groupsInCategory.map((g) => g.group)
                            );
                            const renderRow = (
                              entry: (typeof groupsInCategory)[number]
                            ) => {
                              const groupKey =
                                GROUP_KEY_MAP[entry.group] ?? 'group_other';
                              const groupIcon = GROUP_ICON_MAP[entry.group];
                              const a = entry.permissions.filter((p) =>
                                local.has(p.name)
                              ).length;
                              return (
                                <CategoryRow
                                  active={
                                    selection.kind === 'group' &&
                                    selection.id === entry.group
                                  }
                                  onClick={() =>
                                    setSelection({
                                      kind: 'group',
                                      id: entry.group
                                    })
                                  }
                                  icon={groupIcon}
                                  label={t(groupKey)}
                                  count={a}
                                  total={entry.permissions.length}
                                  indent
                                />
                              );
                            };
                            return (
                              groupsInCategory
                                // Skip child groups whose parent is in this
                                // same category — they're rendered nested
                                // under the parent below.
                                .filter((g) => {
                                  const parent = CHILD_GROUP_MAP[g.group];
                                  return !parent || !inCategory.has(parent);
                                })
                                .map((parentEntry) => {
                                  const children = groupsInCategory.filter(
                                    (c) =>
                                      CHILD_GROUP_MAP[c.group] ===
                                      parentEntry.group
                                  );
                                  return (
                                    <div
                                      key={parentEntry.group}
                                      className='space-y-0.5'
                                    >
                                      {renderRow(parentEntry)}
                                      {children.length > 0 && (
                                        <div className='ms-3 space-y-0.5 border-s ps-1.5'>
                                          {children.map((child) => (
                                            <div key={child.group}>
                                              {renderRow(child)}
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })
                            );
                          })()}
                        </div>
                      </CollapsibleContent>
                    </Collapsible>
                  );
                }
              )}

              {uncategorizedGroups.length > 0 && (
                <div className='mt-2 border-t pt-1.5'>
                  <p className='text-muted-foreground px-2 pb-1 text-[10px] font-semibold tracking-wide uppercase'>
                    {t('categoryUncategorized')}
                  </p>
                  {uncategorizedGroups.map(({ group, permissions: perms }) => {
                    const groupKey = GROUP_KEY_MAP[group] ?? 'group_other';
                    const groupIcon = GROUP_ICON_MAP[group];
                    const a = perms.filter((p) => local.has(p.name)).length;
                    return (
                      <CategoryRow
                        key={group}
                        active={
                          selection.kind === 'group' && selection.id === group
                        }
                        onClick={() =>
                          setSelection({ kind: 'group', id: group })
                        }
                        icon={groupIcon}
                        label={t(groupKey)}
                        count={a}
                        total={perms.length}
                      />
                    );
                  })}
                </div>
              )}

              {sidebarGroupsByCategory.every(
                (c) => c.groupsInCategory.length === 0
              ) &&
                uncategorizedGroups.length === 0 && (
                  <p className='text-muted-foreground py-3 text-center text-xs'>
                    {t('noPermissionsFound')}
                  </p>
                )}
            </div>
          </ScrollArea>
        </aside>

        {/* Main — selected category contents */}
        <ScrollArea className='min-h-0 flex-1 pe-2'>
          <div className='space-y-1.5 py-1'>
            {visibleGroups.length === 0 ? (
              <p className='text-muted-foreground py-8 text-center text-sm'>
                {t('noPermissionsFound')}
              </p>
            ) : (
              visibleGroups.map((entry) => {
                if (hasVisibleParent(entry.group)) return null;
                return renderGroup(entry, false);
              })
            )}
          </div>
        </ScrollArea>
      </div>
    </div>
  );
}

function PermRow({
  label,
  description,
  checked,
  onChange,
  mono,
  disabled
}: {
  label: string;
  description?: string;
  checked: boolean;
  onChange: () => void;
  mono?: boolean;
  disabled?: boolean;
}) {
  return (
    <div className='hover:bg-muted/40 flex items-center gap-3 rounded-md px-2 py-1 transition-colors'>
      <div className='min-w-0 flex-1'>
        <span
          className={cn(
            'block truncate text-sm',
            mono && 'text-muted-foreground font-mono text-xs'
          )}
        >
          {label}
        </span>
        {description && (
          <p className='text-muted-foreground truncate text-xs'>
            {description}
          </p>
        )}
      </div>
      <Switch
        checked={checked}
        disabled={disabled}
        onCheckedChange={onChange}
      />
    </div>
  );
}

function Section({
  title,
  children
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className='border-t pt-1'>
      <p className='text-muted-foreground mb-0.5 px-2 text-[10px] font-semibold tracking-wide uppercase'>
        {title}
      </p>
      <div>{children}</div>
    </div>
  );
}

function CategoryRow({
  active,
  onClick,
  icon,
  label,
  count,
  total,
  indent
}: {
  active: boolean;
  onClick: () => void;
  icon?: string;
  label: string;
  count: number;
  total: number;
  indent?: boolean;
}) {
  const hasActive = count > 0;
  const allActive = total > 0 && count === total;
  return (
    <button
      type='button'
      onClick={onClick}
      className={cn(
        'group flex w-full flex-1 items-center gap-2 rounded-md px-2 py-1.5 text-start text-sm transition-colors',
        indent && 'text-xs',
        active ? 'bg-primary/10 text-primary' : 'text-foreground hover:bg-muted'
      )}
    >
      {icon ? (
        <Icon
          icon={icon}
          className={cn(
            'h-3.5 w-3.5 shrink-0',
            active
              ? 'text-primary'
              : allActive
                ? 'text-emerald-600 dark:text-emerald-400'
                : 'text-muted-foreground'
          )}
        />
      ) : (
        <span className='inline-block h-3.5 w-3.5 shrink-0' />
      )}
      <span className='flex-1 truncate'>{label}</span>
      <span
        className={cn(
          'shrink-0 text-xs tabular-nums',
          active
            ? 'text-primary'
            : allActive
              ? 'text-emerald-600 dark:text-emerald-400'
              : hasActive
                ? 'text-foreground'
                : 'text-muted-foreground'
        )}
      >
        {count}/{total}
      </span>
    </button>
  );
}

function BulkChip({
  label,
  activeCount,
  totalCount,
  onClick,
  variant = 'default'
}: {
  label: string;
  activeCount: number;
  totalCount: number;
  onClick: () => void;
  variant?: 'default' | 'primary';
}) {
  const allActive = totalCount > 0 && activeCount === totalCount;
  return (
    <button
      type='button'
      onClick={onClick}
      className={cn(
        'flex shrink-0 items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-medium whitespace-nowrap transition-colors',
        allActive
          ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
          : variant === 'primary'
            ? 'border-primary/40 bg-primary/5 text-primary hover:bg-primary/10'
            : 'border-border text-muted-foreground hover:bg-muted'
      )}
    >
      {allActive ? (
        <Check className='h-3.5 w-3.5' />
      ) : (
        <span className='inline-block h-3.5 w-3.5' />
      )}
      <span>{label}</span>
      <span className='tabular-nums opacity-70'>
        {activeCount}/{totalCount}
      </span>
    </button>
  );
}

function FilterPill({
  active,
  onClick,
  children
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type='button'
      onClick={onClick}
      className={cn(
        'h-7 shrink-0 rounded-full border px-3 text-xs font-medium whitespace-nowrap transition-colors',
        active
          ? 'border-primary/40 bg-primary/10 text-primary'
          : 'border-border text-muted-foreground hover:bg-muted'
      )}
    >
      {children}
    </button>
  );
}

// Minimal shape we need from a next-intl translator: lookup + raw access.
// Avoids `any` while staying compatible with both root and namespaced t().
type TranslateFn = {
  (key: string): string;
  has: (key: string) => boolean;
  raw: (key: string) => unknown;
};

/**
 * Reads a translation only when the message has no ICU placeholders. Returns
 * `undefined` if the key is missing OR the message expects parameters (e.g.
 * `"{count} boxes"`), so the caller can fall through to the next lookup.
 */
function readPlainMessage(t: TranslateFn, key: string): string | undefined {
  if (!t.has(key)) return undefined;
  const raw = t.raw(key);
  if (typeof raw !== 'string') return undefined;
  if (/\{[^}]+\}/.test(raw)) return undefined;
  return t(key);
}
